import { type FC } from "react";
import { cn } from "../../lib/utils";
import {
	Checkbox,
	Icon,
	type IconColor,
	type IconType,
	List,
	type ListItemProps,
	type ListProps,
} from "../../primitives";

export type ChecklistItemState = "incomplete" | "complete";

export type ChecklistProps = ListProps;

export type ChecklistItemProps = Omit<ListItemProps, "leading" | "onChange"> & {
	state: ChecklistItemState;
	/**
	 * Makes the item interactive: the marker becomes a checkbox and fires with the next state when
	 * toggled. Omit for a read-only status item, which renders a static check icon instead.
	 */
	onChange?: (complete: boolean) => void;
	/** Disables the interactive checkbox. Ignored for read-only items. */
	disabled?: boolean;
	/** Icon for the read-only status marker. Defaults to `check-circle`. Ignored when `onChange` is set. */
	icon?: IconType;
};

const STATE_ICON_COLOR: Record<ChecklistItemState, IconColor> = {
	incomplete: "secondary",
	complete: "confirm",
};

const Root: FC<ChecklistProps> = (props) => <List {...props} />;
Root.displayName = "Checklist";

const Item: FC<ChecklistItemProps> = ({
	state,
	onChange,
	disabled,
	icon = "check-circle",
	className,
	children,
	ref,
	...rest
}) => {
	const marker = onChange ? (
		<Checkbox size="sm" checked={state === "complete"} onChange={onChange} disabled={disabled} />
	) : (
		<Icon type={icon} filled={state === "complete"} size="mini" color={STATE_ICON_COLOR[state]} />
	);

	return (
		<List.Item ref={ref} className={cn("text-tertiary", className)} leading={marker} {...rest}>
			{children}
		</List.Item>
	);
};
Item.displayName = "Checklist.Item";

export const Checklist = Object.assign(Root, { Item });
